import { FormElement, SelectableFormElement, useFormElement } from '@common';
import { objectsEqual } from '@core';
import { computed, onMounted, toRef, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { StatisticsKind, StatisticsSource, StatisticsWizardParcelDataState } from '../enums';
import type { SoilTemperatureStatisticsParcel, StatisticsWizardParcelData } from '../models';
import { useStatisticsWizardStore } from '../stores';
import { statisticsKindFieldNameMap } from '../utils';
import { useI18n } from 'vue-i18n';

export const useStatisticsWizardParcelCard = (parcel: StatisticsWizardParcelData) => {
  const { t } = useI18n();
  const statisticsWizardStore = useStatisticsWizardStore();
  const { statisticsData, needMetadataUpdate } = storeToRefs(statisticsWizardStore);

  let previewPromise: Promise<any> | undefined;

  const parcelItem: Ref<StatisticsWizardParcelData> = toRef(() => parcel);

  const form = parcelItem.value.form;
  const { getChild, validate } = useFormElement(form.element);

  const vectorGroupActiveSubLayerName = computed(() => {
    return parcelItem.value.vectorLayerGroup?.activeSubLayer?.name;
  });

  const yieldField = getChild('yield') as SelectableFormElement;
  const zoneStrengthField = getChild('zoneStrength') as SelectableFormElement;
  const fromDateField = getChild('fromDate') as FormElement;
  const toDateField = getChild('toDate') as FormElement;
  const appliedDoseField = getChild('appliedDose') as SelectableFormElement;
  const stemNumberField = getChild('stemNumber') as SelectableFormElement;
  const soilSampleField = getChild('soilSample') as SelectableFormElement;

  const { value: yieldValue } = useFormElement(yieldField);
  const { value: zoneStrengthValue } = useFormElement(zoneStrengthField);
  const { value: appliedDoseValue } = useFormElement(appliedDoseField);
  const { value: stemNumberValue } = useFormElement(stemNumberField);
  const { value: soilSampleValue } = useFormElement(soilSampleField);

  const isValid = computed(() => parcelItem.value.state === StatisticsWizardParcelDataState.VALID);
  const isInvalidInput = computed(() => parcelItem.value.state === StatisticsWizardParcelDataState.INVALID_INPUT);
  const isInvalidStatisticsKind = computed(
    () => parcelItem.value.state === StatisticsWizardParcelDataState.INVALID_STATISTICS_KIND
  );
  const sourceMissing = computed(() => {
    if (!statisticsData.value.kind) {
      return true;
    }

    if (statisticsData.value.kind === StatisticsKind.SOIL_TEMPERATURE) {
      return false;
    }

    const yieldItems = yieldField.items;

    const secoundaryField = getChild(statisticsKindFieldNameMap[statisticsData.value.kind]!) as SelectableFormElement;

    return !yieldItems.length || !secoundaryField.items.length;
  });

  const handleSoilTemperatureSourceCopy = async () => {
    if (!statisticsData.value.kind || !statisticsData.value.parcels) return;

    switch (statisticsData.value.kind) {
      case StatisticsKind.SOIL_TEMPERATURE:
        {
          const parcelMetadata = (await statisticsWizardStore.getParcelMetadataByParcelId(
            parcelItem.value.id
          )) as SoilTemperatureStatisticsParcel;
          if (!parcelMetadata) return;

          needMetadataUpdate.value = false;

          fromDateField.value = parcelMetadata.dateFrom;
          toDateField.value = parcelMetadata.dateTo;
        }
        break;
      default:
        break;
    }
  };

  watch(
    appliedDoseValue,
    async (newValue, oldValue) => {
      if (parcelItem.value.copied || objectsEqual(oldValue, newValue)) {
        return;
      }

      if (previewPromise) {
        await previewPromise;
        previewPromise = undefined;
      }

      previewPromise = statisticsWizardStore.setupParcelVectorMapWithMultipleSources(
        parcel,
        appliedDoseValue.value,
        StatisticsSource.APPLIED_DOSE
      );
      statisticsWizardStore.updateMapInfos();
    },
    { immediate: true }
  );

  watch(
    stemNumberValue,
    async (newValue, oldValue) => {
      if (parcelItem.value.copied || objectsEqual(oldValue, newValue)) {
        return;
      }

      if (previewPromise) {
        await previewPromise;
        previewPromise = undefined;
      }

      previewPromise = statisticsWizardStore.setupParcelVectorMapWithMultipleSources(
        parcel,
        stemNumberField.value,
        StatisticsSource.STEM_NUMBER
      );
      statisticsWizardStore.updateMapInfos();
    },
    { immediate: true }
  );

  watch(
    soilSampleValue,
    async (newValue, oldValue) => {
      if (parcelItem.value.copied || objectsEqual(oldValue, newValue)) {
        return;
      }

      if (previewPromise) {
        await previewPromise;
        previewPromise = undefined;
      }

      previewPromise = statisticsWizardStore.setupParcelVectorMap(
        parcel,
        soilSampleField.value,
        StatisticsSource.SOIL_SAMPLE
      );
      statisticsWizardStore.updateMapInfos();
    },
    { immediate: true }
  );

  watch(
    zoneStrengthValue,
    async (newValue, oldValue) => {
      if (parcelItem.value.copied || objectsEqual(oldValue, newValue)) {
        return;
      }

      if (previewPromise) {
        await previewPromise;
        previewPromise = undefined;
      }

      previewPromise = statisticsWizardStore.setupParcelVectorMap(
        parcel,
        zoneStrengthField.value,
        StatisticsSource.ZONE_STRENGTH
      );
      statisticsWizardStore.updateMapInfos();
    },
    { immediate: true }
  );

  watch(
    yieldValue,
    async (newValue, oldValue) => {
      if (parcelItem.value.copied || objectsEqual(oldValue, newValue)) {
        return;
      }

      if (previewPromise) {
        await previewPromise;
        previewPromise = undefined;
      }

      previewPromise = statisticsWizardStore.setupParcelVectorMap(parcel, yieldValue.value, StatisticsSource.YIELD);
      statisticsWizardStore.updateMapInfos();
    },
    { immediate: false }
  );

  watch(vectorGroupActiveSubLayerName, (newValue) => {
    if (!newValue) {
      return;
    }
    const currentSubLayerTranslation = t('statistics.label.currentSubLayer');
    appliedDoseField.attributes.hint = `${currentSubLayerTranslation}: ${newValue}`;
    stemNumberField.attributes.hint = `${currentSubLayerTranslation}: ${newValue}`;
  });

  onMounted(async () => {
    if (!parcelItem.value.copied) {
      await statisticsWizardStore.setParcelFormItems(parcelItem.value, false);
      parcelItem.value.isLoading = false;
    }

    if (parcelItem.value.copied) {
      await handleSoilTemperatureSourceCopy();
    }
    await validate();
  });

  return {
    form,
    parcelItem,
    isValid,
    isInvalidInput,
    isInvalidStatisticsKind,
    isLoading: parcelItem.value.isLoading,
    sourceMissing
  };
};
